import { useState, useCallback, useEffect } from 'react';

// ── Admin credentials ─────────────────────────────────────────────────────────
const ADMIN_EMAIL    = 'Jillersaint@softypanda.com';
const ADMIN_PASSWORD = 'Joshlover18@';
const ADMIN_PLAN     = 'Golden Pro';
const SESSION_KEY    = 'openclaw_admin_session';

export interface AdminUser {
  email: string;
  plan: string;
  isAdmin: boolean;
  loginAt: number;
}

export interface AdminAuthResult {
  user: AdminUser | null;
  isAdmin: boolean;
  isLoggedIn: boolean;
  login: (email: string, password: string) => { success: boolean; isAdmin: boolean };
  logout: () => void;
}

export function useAdminAuth(): AdminAuthResult {
  const [user, setUser] = useState<AdminUser | null>(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) return JSON.parse(raw) as AdminUser;
    } catch { /* ignore */ }
    return null;
  });

  // Keep in sync across tabs
  useEffect(() => {
    const handler = () => {
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        setUser(raw ? (JSON.parse(raw) as AdminUser) : null);
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const login = useCallback((email: string, password: string): { success: boolean; isAdmin: boolean } => {
    const isAdminLogin =
      email.trim().toLowerCase() === ADMIN_EMAIL.toLowerCase() &&
      password === ADMIN_PASSWORD;

    if (isAdminLogin) {
      const newUser: AdminUser = {
        email: ADMIN_EMAIL,
        plan: ADMIN_PLAN,
        isAdmin: true,
        loginAt: Date.now(),
      };
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(newUser));
        // Clear word-usage limits so admin starts clean
        localStorage.removeItem('openclaw_words_used');
      } catch { /* ignore */ }
      setUser(newUser);
      return { success: true, isAdmin: true };
    }

    // For any other email/password just mark as regular logged-in user
    const regularUser: AdminUser = {
      email: email.trim(),
      plan: 'Free Trial',
      isAdmin: false,
      loginAt: Date.now(),
    };
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(regularUser));
    } catch { /* ignore */ }
    setUser(regularUser);
    return { success: true, isAdmin: false };
  }, []);

  const logout = useCallback(() => {
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    setUser(null);
  }, []);

  return {
    user,
    isAdmin: user?.isAdmin === true,
    isLoggedIn: user !== null,
    login,
    logout,
  };
}
